// ----------------------------------------------------------------------- imports externos
import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { traerAlertas, guardarAlerta } from "../services/alertas";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Alertas: qué avisa el sistema, a quién y cuándo.
 *
 * Nació de una observación de Igna (01/09): «tenemos muchas alertas» — y no
 * había ningún lugar donde verlas todas juntas. Esta pantalla es ese lugar:
 * el catálogo entero, con su cadencia, sus destinos y su agenda reales.
 *
 * DOS CLASES DE FILA, Y ES HONESTO DECIRLO: las `editable` se gobiernan desde
 * acá (el bot lee la tabla antes de mandar — cambiar un mail, los días o la
 * franja rige sin deploy); las demás MUESTRAN sus valores pero los gobierna el
 * código del bot. Mentir que todo es editable sería peor que la mezcla.
 *
 * LA AGENDA (Igna, 04/09): por alerta se elige qué días avisa (con presets
 * Semanal y Fin de semana) y en qué franja horaria (o todo el día). La hora
 * exacta de disparo de las programadas sigue en el código; esto la ACOTA.
 *
 * Ver pide `ver.sistema`; tocar pide `admin.permisos` (la pared es la RLS,
 * la pantalla la refleja).
 */

// Días de la semana en dígitos ISO: 1=Lunes … 7=Domingo. Las dos "M" se
// distinguen por el title (Martes / Miércoles).
const DIAS = [
  ["1", "L", "Lunes"],
  ["2", "M", "Martes"],
  ["3", "M", "Miércoles"],
  ["4", "J", "Jueves"],
  ["5", "V", "Viernes"],
  ["6", "S", "Sábado"],
  ["7", "D", "Domingo"],
];

const hhmm = (v) => (v ? String(v).slice(0, 5) : "");

function resumenDias(dias) {
  const d = (dias || "").trim();
  if (!d) return "Todos los días";
  if (d === "12345") return "Lun a Vie";
  if (d === "67") return "Sáb y Dom";
  const map = { 1: "L", 2: "M", 3: "M", 4: "J", 5: "V", 6: "S", 7: "D" };
  return d
    .split("")
    .map((n) => map[n] || "")
    .filter(Boolean)
    .join("·");
}

function resumenAgenda(a) {
  const franja =
    a.hora_desde && a.hora_hasta
      ? `${hhmm(a.hora_desde)}–${hhmm(a.hora_hasta)}`
      : "todo el día";
  return `${resumenDias(a.dias)} · ${franja}`;
}

const Alertas = () => {
  const { permisos = null } = useOutletContext() || {};
  const permitido = permisos !== null && permisos.includes("ver.sistema");
  const puedeEditar = permisos !== null && permisos.includes("admin.permisos");

  const [datos, setDatos] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState("");

  useEffect(() => {
    if (!permitido) return;
    traerAlertas()
      .then(setDatos)
      .catch(() => setError(true));
  }, [permitido]);

  const abrirEditor = (a) => {
    setFallo("");
    setEditando({
      clave: a.clave,
      destinos: a.destinos || "",
      dias: a.dias || "",
      todoElDia: !(a.hora_desde && a.hora_hasta),
      horaDesde: hhmm(a.hora_desde) || "08:00",
      horaHasta: hhmm(a.hora_hasta) || "18:00",
    });
  };

  const toggleDia = (n) => {
    const set = new Set((editando.dias || "").split("").filter(Boolean));
    if (set.has(n)) set.delete(n);
    else set.add(n);
    setEditando({ ...editando, dias: [...set].sort().join("") });
  };

  const guardar = async () => {
    if (!editando) return;
    const limpio = editando.destinos
      .split(",")
      .map((d) => d.trim())
      .filter(Boolean)
      .join(", ");
    if (!limpio) {
      // Sin destinos la alerta caería al valor de fábrica en silencio; mejor
      // decirlo acá que descubrirlo cuando un aviso llegue a otro lado.
      setFallo("Dejá al menos un mail — sin destinos vuelve al de fábrica.");
      return;
    }
    let hora_desde = null;
    let hora_hasta = null;
    if (!editando.todoElDia) {
      if (!editando.horaDesde || !editando.horaHasta) {
        setFallo("Completá desde y hasta, o marcá «todo el día».");
        return;
      }
      hora_desde = editando.horaDesde;
      hora_hasta = editando.horaHasta;
    }
    setGuardando(true);
    setFallo("");
    try {
      const fila = await guardarAlerta(editando.clave, {
        destinos: limpio,
        dias: editando.dias,
        hora_desde,
        hora_hasta,
      });
      setDatos((ds) => ds.map((a) => (a.clave === fila.clave ? fila : a)));
      setEditando(null);
    } catch {
      setFallo("No se pudo guardar. Probá de nuevo en un rato.");
    } finally {
      setGuardando(false);
    }
  };

  const prender = async (a, activa) => {
    setFallo("");
    try {
      const fila = await guardarAlerta(a.clave, { activa });
      setDatos((ds) => ds.map((x) => (x.clave === fila.clave ? fila : x)));
    } catch {
      setFallo("No se pudo cambiar el encendido. Probá de nuevo en un rato.");
    }
  };

  if (permisos !== null && !permitido) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      {fallo && <div className="panel-aviso">{fallo}</div>}

      {datos !== undefined && (
        <div className="tabla-wrap">
          <table className="tabla fija">
            <thead>
              <tr>
                <th>Alerta</th>
                <th>Cuándo</th>
                <th>A quién</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {datos.map((a) => {
                const edit = editando?.clave === a.clave;
                return (
                  <tr key={a.clave} className={a.activa ? "" : "tenue"}>
                    <td>
                      <b>{a.nombre}</b>
                      {a.descripcion && (
                        <div className="sub">{a.descripcion}</div>
                      )}
                    </td>

                    <td>
                      {a.cadencia}
                      {edit ? (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                            {DIAS.map(([n, letra, titulo]) => {
                              const on = (editando.dias || "").includes(n);
                              return (
                                <button
                                  key={n}
                                  type="button"
                                  className="btn"
                                  title={titulo}
                                  aria-label={titulo}
                                  aria-pressed={on}
                                  onClick={() => toggleDia(n)}
                                  style={{
                                    minWidth: 30,
                                    fontWeight: 700,
                                    background: on ? "var(--verde, #16a34a)" : "",
                                    color: on ? "#fff" : "",
                                  }}
                                >
                                  {letra}
                                </button>
                              );
                            })}
                          </div>
                          <div style={{ marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                            <button type="button" className="btn"
                              onClick={() => setEditando({ ...editando, dias: "" })}>
                              Todos
                            </button>
                            <button type="button" className="btn"
                              onClick={() => setEditando({ ...editando, dias: "12345" })}>
                              Semanal (L–V)
                            </button>
                            <button type="button" className="btn"
                              onClick={() => setEditando({ ...editando, dias: "67" })}>
                              Fin de semana (S–D)
                            </button>
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <label>
                              <input
                                type="checkbox"
                                checked={editando.todoElDia}
                                aria-label="Todo el día"
                                onChange={(e) =>
                                  setEditando({ ...editando, todoElDia: e.target.checked })
                                }
                              />{" "}
                              Todo el día
                            </label>
                            {!editando.todoElDia && (
                              <div style={{ marginTop: 4 }}>
                                <input
                                  type="time"
                                  aria-label="Desde"
                                  value={editando.horaDesde}
                                  onChange={(e) =>
                                    setEditando({ ...editando, horaDesde: e.target.value })
                                  }
                                />{" "}
                                a{" "}
                                <input
                                  type="time"
                                  aria-label="Hasta"
                                  value={editando.horaHasta}
                                  onChange={(e) =>
                                    setEditando({ ...editando, horaHasta: e.target.value })
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        a.editable && (
                          <div className="sub">{resumenAgenda(a)}</div>
                        )
                      )}
                    </td>

                    <td>
                      {edit ? (
                        <>
                          <input
                            aria-label={`Destinos de ${a.nombre}`}
                            value={editando.destinos}
                            onChange={(e) =>
                              setEditando({ ...editando, destinos: e.target.value })
                            }
                            style={{ width: "100%" }}
                          />
                          <button onClick={guardar} disabled={guardando} className="btn">
                            Guardar
                          </button>{" "}
                          <button onClick={() => setEditando(null)} className="btn">
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          {a.destinos || <span className="tenue">—</span>}
                          {a.editable && puedeEditar && (
                            <button
                              aria-label={`Editar alerta ${a.nombre}`}
                              title="Editar destinos, días y horario"
                              className="btn"
                              onClick={() => abrirEditor(a)}
                            >
                              ✏️
                            </button>
                          )}
                        </>
                      )}
                    </td>

                    <td>
                      {a.editable ? (
                        puedeEditar ? (
                          <button className="btn" onClick={() => prender(a, !a.activa)}>
                            {a.activa ? "🟢 Prendida" : "⚪ Apagada"}
                          </button>
                        ) : (
                          <span>{a.activa ? "🟢 Prendida" : "⚪ Apagada"}</span>
                        )
                      ) : (
                        <span className="tenue" title="Se gobierna desde el código del bot">
                          fija por código
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </>
  );
};

export default Alertas;
